import React from 'react';
import { Users, Award, Lightbulb, Heart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export const EducatorLed = () => {
  const features = [
    {
      icon: Users,
      title: 'Academic Senate',
      description: 'Teachers lead school policy, professional development, and educational standards through an elected Academic Senate.',
      color: 'tealAccent'
    },
    {
      icon: Lightbulb,
      title: 'Curriculum Design',
      description: 'Educators design and integrate research-based curricula tailored to student needs, community input, and real-world applications.',
      color: 'goldenOlive'
    },
    {
      icon: Award,
      title: 'Professional Excellence',
      description: 'Competitive compensation, comprehensive benefits, and ongoing professional development opportunities for all educators.',
      color: 'coralRed'
    },
    {
      icon: Heart,
      title: 'Work-Life Balance',
      description: 'Innovative AM/PM scheduling model supports teacher well-being with mid-day collaboration time and flexible schedules.',
      color: 'sageGreen'
    }
  ];

  return (
    <section id="educators" className="py-20 px-4 sm:px-6 lg:px-8 bg-navyBlue/5">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <div className="inline-block bg-tealAccent/10 px-6 py-2 rounded-full mb-4">
            <span className="text-tealAccent font-semibold">What Makes Us Different</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">Educator-Led Innovation</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            At Doctrina, we believe that <span className="font-semibold text-tealAccent">teachers are the key to student success</span>. Our unique governance model empowers educators to lead, innovate, and shape the future of education.
          </p>
        </div>

        {/* Academic Senate Image */}
        <div className="mb-16 relative rounded-3xl overflow-hidden shadow-2xl">
          <img 
            src={process.env.REACT_APP_ACADEMIC_SENATE_URL} 
            alt="Educators collaborating in Academic Senate meeting" 
            className="w-full h-full object-cover" style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-tealAccent/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">Teachers Leading the Way</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Our elected Academic Senate empowers educators to make key decisions on curriculum, policy, and professional development—ensuring teacher voice is at the heart of every decision.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white group">
                <CardHeader>
                  <div className={`bg-${feature.color}/10 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`text-${feature.color}`} size={28} />
                  </div>
                  <CardTitle className="text-xl text-navyBlue">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-16 bg-white rounded-2xl p-8 sm:p-12 shadow-lg">
          <h3 className="text-2xl font-bold text-navyBlue mb-6 text-center">Empowering Teachers, Transforming Education</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-tealAccent mb-2">Teacher Voice</div>
              <p className="text-gray-600">Elected Academic Senate makes key decisions on curriculum and policy</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-goldenOlive mb-2">Collaboration</div>
              <p className="text-gray-600">Mid-day overlap for co-teaching, PLC meetings, and student intervention</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-coralRed mb-2">Innovation</div>
              <p className="text-gray-600">Teachers design research-based practices integrating community needs</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EducatorLed;